import { useEffect, useMemo, useRef, useState } from 'react';
import { searchContext, userAppContext } from '@/context/Context';
import { useMappingStackStore } from '@/store/mappingStackStore';
import type { MappingLevelContent } from '@/types/mappingDrillDown';
import { buildNumPmidMappingConfig } from '@/utils/evidenceTimelineMapping';
import {
  fetchPolarityCounts,
  hasPolarityAssociationScope,
  type PolarityCounts,
  type PolaritySide,
} from '../utils/polarityBalance';

type PolarityBalanceProps = {
  row: Record<string, unknown> | null | undefined;
  source: string;
  tab: string;
  onClose?: () => void;
  ensureTablesViewForDrilldown?: () => void;
  searchParams?: URLSearchParams | { toString(): string };
  setSearchParams?: (params: URLSearchParams) => void;
  className?: string;
};

const POLARITY_TONES = {
  Positive: {
    fill: '#3AA58C',
    surface: '#E8F6F2',
    text: '#1B6B58',
  },
  Negative: {
    fill: '#D67C8A',
    surface: '#FBF1F3',
    text: '#8C4654',
  },
} as const;

function unlabeledCaption(count: number): string {
  if (count === 1) return '1 sentence has no polarity';
  return `${count.toLocaleString()} sentences have no polarity`;
}

function shareLabel(count: number, total: number): string {
  if (total <= 0 || count <= 0) return '0%';
  const pct = (count / total) * 100;
  if (pct < 0.1) return '<0.1%';
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

export function PolarityBalance({
  row,
  source,
  tab,
  onClose,
  ensureTablesViewForDrilldown,
  searchParams,
  setSearchParams,
  className,
}: PolarityBalanceProps) {
  const userCtx = userAppContext() as {
    authorization?: unknown;
    setAuthError?: (error: unknown) => void;
    isAuthenticated?: boolean;
  } | null;
  const searchCtx = (searchContext() ?? {}) as {
    view?: string;
    searchItems?: Array<{ chemID?: string; chemPrefName?: string }>;
    setMappingContent?: (content: Record<string, unknown> | (() => Record<string, unknown>)) => void;
  };

  const view = searchCtx.view ?? '';
  const searchItems = searchCtx.searchItems ?? [];
  const normalizedTab = String(tab ?? '').toUpperCase();

  const mappingConfig = useMemo(() => {
    if (!row || (normalizedTab !== 'GDA' && normalizedTab !== 'VDA')) return null;
    const config = buildNumPmidMappingConfig(row, view, normalizedTab, searchItems);
    return hasPolarityAssociationScope(config) ? config : null;
  }, [normalizedTab, row, searchItems, view]);

  const associationKey = mappingConfig ? JSON.stringify(mappingConfig.mappingItems) : '';
  const rowRef = useRef(row);
  rowRef.current = row;
  const searchItemsRef = useRef(searchItems);
  searchItemsRef.current = searchItems;
  const authRef = useRef(userCtx);
  authRef.current = userCtx;

  const [counts, setCounts] = useState<PolarityCounts | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  useEffect(() => {
    const currentRow = rowRef.current;
    if (!associationKey || !currentRow) {
      setCounts(prev => (prev == null ? prev : null));
      setStatus(prev => (prev === 'idle' ? prev : 'idle'));
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setCounts(null);
    const auth = authRef.current;

    fetchPolarityCounts({
      row: currentRow,
      view,
      tab: normalizedTab,
      source,
      searchItems: searchItemsRef.current,
      authorization: auth?.authorization,
      isAuthenticated: Boolean(auth?.isAuthenticated),
      setAuthError: auth?.setAuthError ?? (() => {}),
    })
      .then(next => {
        if (cancelled) return;
        setCounts(next);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCounts(null);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [associationKey, normalizedTab, source, userCtx?.authorization, userCtx?.isAuthenticated, view]);

  const openPolarity = (side: PolaritySide) => {
    if (!row || !mappingConfig) return;
    const count = side === 'Positive' ? counts?.positive : counts?.negative;
    if (!count) return;

    const mappingPayload: MappingLevelContent = {
      ...mappingConfig,
      selectedPolarity: side,
    };
    const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';

    const leaveDetail = () => {
      if (searchParams && setSearchParams) {
        const nextParams = new URLSearchParams(searchParams.toString());
        if (nextParams.get('fullRecord') === '1') {
          nextParams.delete('fullRecord');
          setSearchParams(nextParams);
        }
      }
      onClose?.();
    };

    ensureTablesViewForDrilldown?.();

    if (useDrillDownMapping) {
      window.setTimeout(() => {
        const store = useMappingStackStore.getState();
        const needsBase = store.stack.length === 0 || !store.stack[0]?.isBase;
        if (needsBase) {
          store.resetStack({
            id: `base-${normalizedTab || 'GDA'}`,
            label: 'Results',
            isBase: true,
            searchItems: (searchItems as Array<{ id: string; name: string }>) ?? [],
            panelTab: normalizedTab || 'GDA',
            isEvidence: false,
          });
        }
        const latest = useMappingStackStore.getState();
        const current = latest.stack[latest.currentIndex];
        const isCurrentPolarity =
          current != null && !current.isBase && current.mappingContent?.selectedPolarity != null;
        if (isCurrentPolarity) {
          latest.clearFromLevel(latest.currentIndex);
        }
        useMappingStackStore.getState().pushLevel({
          id: `level-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          label: `Polarity · ${side}`,
          isBase: false,
          mappingContent: mappingPayload,
        });
        leaveDetail();
      }, 0);
      return;
    }

    if (typeof searchCtx.setMappingContent === 'function') {
      searchCtx.setMappingContent(() => ({
        isActive: true,
        ...mappingPayload,
      }));
    }
    leaveDetail();
  };

  if (!mappingConfig) return null;

  return (
    <section
      className={`min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white ${className ?? ''}`}
      data-testid="polarity-balance"
      aria-busy={status === 'loading'}
    >
      <h3 className="flex min-h-[2.25rem] items-center border-b border-slate-200 bg-gradient-to-r from-pink-50 to-white px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">
        Sentence polarity
      </h3>
      <div className="px-3 py-3">
        {status === 'loading' && (
          <div className="space-y-3" aria-hidden="true">
            <div className="h-2.5 animate-pulse rounded-full bg-slate-100" />
            <div className="flex gap-2">
              <div className="h-7 w-28 animate-pulse rounded-full bg-slate-100" />
              <div className="h-7 w-28 animate-pulse rounded-full bg-slate-100" />
            </div>
          </div>
        )}
        {status === 'error' && (
          <p className="mb-0 text-xs text-slate-500">Polarity counts are unavailable.</p>
        )}
        {status === 'ready' && counts && (
          <PolarityBar counts={counts} onOpen={openPolarity} />
        )}
      </div>
    </section>
  );
}

function PolarityBar({
  counts,
  onOpen,
}: {
  counts: PolarityCounts;
  onOpen: (side: PolaritySide) => void;
}) {
  const { positive, negative, unlabeled } = counts;
  const hasLabeled = positive > 0 || negative > 0;

  const labeled = positive + negative;

  return (
    <div>
      {hasLabeled ? (
        <div
          className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-slate-100 p-px"
          aria-hidden="true"
        >
          {positive > 0 && (
            <div
              className="h-full rounded-full"
              style={{
                flexGrow: positive,
                flexBasis: 0,
                minWidth: 6,
                backgroundColor: POLARITY_TONES.Positive.fill,
              }}
            />
          )}
          {negative > 0 && (
            <div
              className="h-full rounded-full"
              style={{
                flexGrow: negative,
                flexBasis: 0,
                minWidth: 6,
                backgroundColor: POLARITY_TONES.Negative.fill,
              }}
            />
          )}
        </div>
      ) : (
        <p className="mb-0 text-xs text-slate-500">No sentences with polarity for this association.</p>
      )}
      {hasLabeled && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <PolarityLegend
            side="Positive"
            count={positive}
            share={shareLabel(positive, labeled)}
            onOpen={onOpen}
          />
          <PolarityLegend
            side="Negative"
            count={negative}
            share={shareLabel(negative, labeled)}
            onOpen={onOpen}
          />
          {unlabeled > 0 && (
            <p className="mb-0 inline-flex items-center gap-2 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              <span className="h-2 w-2 shrink-0 rounded-full bg-slate-300" aria-hidden="true" />
              {unlabeledCaption(unlabeled)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function PolarityLegend({
  side,
  count,
  share,
  onOpen,
}: {
  side: PolaritySide;
  count: number;
  share: string;
  onOpen: (side: PolaritySide) => void;
}) {
  const tone = POLARITY_TONES[side];

  if (count === 0) {
    return <p className="mb-0 text-xs text-slate-500">{side} 0</p>;
  }

  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors hover:brightness-95"
      style={{
        backgroundColor: tone.surface,
        borderColor: tone.surface,
        color: tone.text,
      }}
      aria-label={`Show ${count.toLocaleString()} ${side.toLowerCase()} sentences`}
      onClick={() => onOpen(side)}
    >
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: tone.fill }} aria-hidden="true" />
      <span>{side}</span>
      <span className="tabular-nums font-semibold">{count.toLocaleString()}</span>
      <span className="tabular-nums text-[11px] opacity-70">{share}</span>
    </button>
  );
}
